function parseBooleanValue(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === "true") {
    return true;
  }
  if (normalizedValue === "false") {
    return false;
  }

  return null;
}

const SOLLIO_COMMENT_AM_UNLOADING_START_HOUR_SETTING_KEY = "sollio_comment_am_unloading_start_hour";

export function toTimeSettingInputValue(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const plainTimeMatch = trimmedValue.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
    if (plainTimeMatch) {
      return `${plainTimeMatch[1]}:${plainTimeMatch[2]}`;
    }

    const embeddedTimeMatch = trimmedValue.match(/[T\s](\d{2}):(\d{2})(?::\d{2})?/);
    if (embeddedTimeMatch) {
      return `${embeddedTimeMatch[1]}:${embeddedTimeMatch[2]}`;
    }
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.valueOf())) {
    return "";
  }

  return `${String(parsedDate.getHours()).padStart(2, "0")}:${String(parsedDate.getMinutes()).padStart(2, "0")}`;
}

function normalizeTimeSettingValue(value) {
  const inputValue = toTimeSettingInputValue(value);
  if (!inputValue) {
    return null;
  }

  const timeMatch = inputValue.match(/^(\d{2}):(\d{2})$/);
  if (!timeMatch) {
    return null;
  }

  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return inputValue;
}

function formatHourSettingValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "";
  }

  const totalMinutes = Math.round(numericValue * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function parseHourSettingValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return null;
  }

  const timeMatch = normalizedValue.match(/^(\d{1,2}):(\d{2})$/);
  if (timeMatch) {
    const hours = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    return hours + (minutes / 60);
  }

  return parseNumberValue(normalizedValue);
}

function normalizeClientProductPairsValue(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const resolvedPairsByLookupKey = new Map();
  value.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const clientName = typeof item.client_name === "string" ? item.client_name.trim() : "";
    const grainType = typeof item.grain_type === "string" ? item.grain_type.trim() : "";
    if (!clientName || !grainType) {
      return;
    }

    resolvedPairsByLookupKey.set(clientName.toLocaleLowerCase("fr-CA"), {
      client_name: clientName,
      grain_type: grainType,
    });
  });

  return Array.from(resolvedPairsByLookupKey.values()).sort((left, right) => {
    const byClientName = left.client_name.localeCompare(right.client_name, "fr-CA", { sensitivity: "base" });
    if (byClientName !== 0) {
      return byClientName;
    }
    return left.grain_type.localeCompare(right.grain_type, "fr-CA", { sensitivity: "base" });
  });
}

function clientProductPairsEqual(left, right) {
  const resolvedLeft = normalizeClientProductPairsValue(left);
  const resolvedRight = normalizeClientProductPairsValue(right);
  if (resolvedLeft.length !== resolvedRight.length) {
    return false;
  }

  return resolvedLeft.every((value, index) => (
    value.client_name === resolvedRight[index].client_name
    && value.grain_type === resolvedRight[index].grain_type
  ));
}

export function buildDraftFromState(state) {
  return {
    settings: Object.fromEntries(
      Array.isArray(state?.settings)
        ? state.settings.map((setting) => [
          setting.key,
          setting.value_type === "client_product_pairs"
            ? normalizeClientProductPairsValue(setting.value)
            : setting.value_type === "boolean"
              ? Boolean(setting.value)
              : setting.value_type === "time"
                ? toTimeSettingInputValue(setting.value)
              : setting.key === SOLLIO_COMMENT_AM_UNLOADING_START_HOUR_SETTING_KEY
                ? formatHourSettingValue(setting.value)
                : String(setting.value ?? ""),
        ])
        : [],
    ),
    timeWindowTolerances: Object.fromEntries(
      Array.isArray(state?.time_window_tolerances)
        ? state.time_window_tolerances.map((tolerance) => [
          tolerance.priority,
          {
            before: String(tolerance.tolerance_before_minutes ?? tolerance.tolerance_minutes ?? ""),
            after: String(tolerance.tolerance_after_minutes ?? tolerance.tolerance_minutes ?? ""),
          },
        ])
        : [],
    ),
  };
}

export function parseNumberValue(value) {
  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  const normalizedValue = String(value).trim().replace(",", ".");
  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

export function buildUpdatePayload(baseState, draft) {
  if (!baseState) {
    return { settings: [], time_window_tolerances: [] };
  }

  const settings = (baseState.settings || []).flatMap((setting) => {
    if (setting.value_type === "client_product_pairs") {
      const nextValue = normalizeClientProductPairsValue(draft.settings[setting.key]);
      if (clientProductPairsEqual(nextValue, setting.value)) {
        return [];
      }
      return [{ key: setting.key, value: nextValue }];
    }

    if (setting.value_type === "boolean") {
      const nextValue = parseBooleanValue(draft.settings[setting.key]);
      if (nextValue == null || nextValue === Boolean(setting.value)) {
        return [];
      }
      return [{ key: setting.key, value: nextValue }];
    }

    if (setting.value_type === "time") {
      const nextValue = normalizeTimeSettingValue(draft.settings[setting.key]);
      if (nextValue == null || nextValue === toTimeSettingInputValue(setting.value)) {
        return [];
      }
      return [{ key: setting.key, value: nextValue }];
    }

    const nextValue = setting.key === SOLLIO_COMMENT_AM_UNLOADING_START_HOUR_SETTING_KEY
      ? parseHourSettingValue(draft.settings[setting.key])
      : parseNumberValue(draft.settings[setting.key]);
    if (nextValue == null || Math.abs(nextValue - Number(setting.value ?? 0)) <= 1e-9) {
      return [];
    }
    return [{ key: setting.key, value: nextValue }];
  });

  const timeWindowTolerances = (baseState.time_window_tolerances || []).flatMap((tolerance) => {
    const currentDraft = draft.timeWindowTolerances[tolerance.priority] || { before: "", after: "" };
    const nextBefore = parseNumberValue(currentDraft.before);
    const nextAfter = parseNumberValue(currentDraft.after);
    if (nextBefore == null || nextAfter == null) {
      return [];
    }

    const currentBefore = Number(tolerance.tolerance_before_minutes ?? tolerance.tolerance_minutes ?? 0);
    const currentAfter = Number(tolerance.tolerance_after_minutes ?? tolerance.tolerance_minutes ?? 0);
    if (nextBefore === currentBefore && nextAfter === currentAfter) {
      return [];
    }

    return [{
      priority: tolerance.priority,
      tolerance_before_minutes: Math.round(nextBefore),
      tolerance_after_minutes: Math.round(nextAfter),
    }];
  });

  return {
    settings,
    time_window_tolerances: timeWindowTolerances,
  };
}

export function groupSettingsBySection(settings) {
  return settings.reduce((sections, setting) => {
    const sectionKey = typeof setting.section === "string" && setting.section.trim()
      ? setting.section.trim()
      : "default";
    if (!sections[sectionKey]) {
      sections[sectionKey] = [];
    }
    sections[sectionKey].push(setting);
    return sections;
  }, {});
}

export function groupSettingsByGroup(settings) {
  return settings.reduce((groups, setting) => {
    const groupKey = typeof setting.group === "string" && setting.group.trim()
      ? setting.group.trim()
      : "default";
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(setting);
    return groups;
  }, {});
}